import _get from 'lodash/get'
import React, { useEffect, useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { View } from 'react-native'
import type { ReadonlyDeep } from 'type-fest'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { PremiumInterval } from '@flows-ui/services/QuoteDataContext.types'
import { useTranslation } from '@flows-ui/utils/i18n'
import type { TextContent } from '@flows/core/i18n/types'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { ProductKey } from '@flows/index'
import Alert from '@ui-kit/components/Alert'

import { ConfigurableParameters } from './ConfigurableParameters'
import { CoveragePlans } from './CoveragePlans'
import { CoverageSection } from './CoverageSection'
import { DocumentsSection } from './DocumentsSection'
import { ExtensionCoverageSection } from './ExtensionCoverageSection'
import { Extensions } from './Extensions'
import type { ExtensionExtraContent } from './Extensions'
import { Header } from './Header'
import { InsuredSumInput } from './InsuredSumInput'
import { Loader } from './Loader'
import { useCoveragePlanSelection, useQuoteLoader } from './hooks'
import { useQuoteStepViewedTracking } from './hooks/useQuoteStepViewedTracking'

export type StepQuoteProps = {
  product: ProductKey
  configurableParameters?: ReadonlyDeep<NonNullable<AnyStepContentElement>[]>
  coverageExtensionKey?: string
  showPriceCalculationLoader?: boolean
  isCoveragePlanSelectorHidden?: boolean
  allowedPremiumIntervals?: readonly PremiumInterval[]
  headerSubtitle?: TextContent
  headerTitle?: TextContent
  extensionExtraContent?: Record<string, ExtensionExtraContent>
  submitHandler: () => void | Promise<void>
  stepContentRenderer: (props: any) => React.ReactElement
  componentMap?: Record<string, React.ElementType>
}

export const StepQuote = (props: StepQuoteProps) => {
  const showLoader = useQuoteLoader(props.showPriceCalculationLoader)
  const { productGeneration } = useQuoteData()
  const { watch } = useFormContext()
  const { t } = useTranslation()

  useQuoteStepViewedTracking(props.product)

  const coveragePlans = useMemo(() => productGeneration?.coveragePlans ?? [], [productGeneration])

  const { coverageExtension, toggleableExtensions } = useMemo(() => {
    const allExtensions = productGeneration?.extensions ?? []
    return {
      coverageExtension: allExtensions.find(
        (extension) => extension.configurationDataExtensionKey === props.coverageExtensionKey
      ),
      toggleableExtensions: allExtensions.filter(
        (extension) => extension.configurationDataExtensionKey !== props.coverageExtensionKey
      ),
    }
  }, [productGeneration, props.coverageExtensionKey])

  const { selectedPlan, configurationParameters } = useCoveragePlanSelection({
    productKey: props.product,
    coveragePlans,
    defaultCoveragePlan: productGeneration?.defaultCoveragePlan ?? null,
  })

  const formValues = watch()
  const naturalDisastersEligible = _get(
    formValues,
    ['products', props.product, 'configuration_parameters', 'natural_disasters_coverage_eligible'],
    true
  )
  const naturalDisastersCoverageSelected = _get(
    formValues,
    ['products', props.product, 'configuration_parameters', 'natural_disasters_coverage'],
    false
  )

  const showNatDisAlert = naturalDisastersEligible === false && naturalDisastersCoverageSelected
  const natDisAlertRef = useRef<View>(null)
  useEffect(() => {
    if (showNatDisAlert && natDisAlertRef.current) {
      ;(natDisAlertRef.current as unknown as HTMLElement).scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [showNatDisAlert])

  if (showLoader) {
    return <Loader />
  }

  const extensions = productGeneration?.extensions ?? []
  const documents = productGeneration?.documents ?? []

  return (
    <View className="mx-auto sm:max-w-[350px] md:max-w-[449px]">
      {showNatDisAlert && (
        <View ref={natDisAlertRef} className="mb-6">
          <Alert
            variant="error"
            message={t('sharedFlows_common:steps.quote.naturalDisastersNotEligible')}
          />
        </View>
      )}
      <Header
        productName={productGeneration?.titleShort ?? ''}
        headerTitle={props.headerTitle}
        headerSubtitle={props.headerSubtitle}
        productKey={productGeneration?.productKey ?? props.product}
        allowedPremiumIntervals={props.allowedPremiumIntervals}
      />
      <CoveragePlans
        productKey={props.product}
        isCoveragePlanSelectorHidden={props.isCoveragePlanSelectorHidden}
      />
      <ConfigurableParameters
        configurableParameters={props.configurableParameters}
        stepContentRenderer={props.stepContentRenderer}
        componentMap={props.componentMap}
        priceWatchProductKey={props.product}
      />
      <InsuredSumInput />
      {toggleableExtensions.length > 0 && (
        <Extensions
          extensions={toggleableExtensions}
          productKey={productGeneration?.productKey ?? props.product}
          extraContent={props.extensionExtraContent}
          stepContentRenderer={props.stepContentRenderer}
          componentMap={props.componentMap}
        />
      )}
      {coverageExtension && <ExtensionCoverageSection extension={coverageExtension} />}
      {!props.coverageExtensionKey && selectedPlan && (
        <CoverageSection
          selectedCoveragePlan={selectedPlan}
          configurationParameters={configurationParameters}
          extensions={extensions}
        />
      )}
      {documents.length > 0 && <DocumentsSection documents={documents} />}
    </View>
  )
}
